import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  ArrowRight, Check, Zap, Globe, Users, BarChart3, 
  FileText, Sparkles, Send, Shield, Clock, TrendingUp, 
  Newspaper, Bot, Database, Plug, Star, ChevronRight,
  Building2, Megaphone, Eye, Target, CreditCard, MessageSquare,
  Settings, PieChart, Link2, Calendar, CheckCircle2, Rocket,
  BookOpen, Rss, Search, Wand2, Play, Lock, Code, ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";

const API = process.env.REACT_APP_BACKEND_URL;

export default function FeaturesPage() {
  const { t, language } = useLanguage();
  const [activeRole, setActiveRole] = useState("advertiser");
  const [stats, setStats] = useState({ portals: 0, articles: 0, advertisers: 0 });
  const [loadingStats, setLoadingStats] = useState(true);

  // Pobierz prawdziwe statystyki z API
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API}/api/public/stats`);
        if (res.ok) {
          const data = await res.json();
          setStats(data);
        }
      } catch (err) {
        console.error("Error fetching stats:", err);
      } finally {
        setLoadingStats(false);
      }
    };
    fetchStats();
  }, []);

  const advertiserFeatures = [
    {
      icon: Globe,
      title: { pl: "Marketplace portali", en: "Portal Marketplace" },
      desc: { pl: "Przeglądaj setki zweryfikowanych portali z różnymi kategoriami tematycznymi. Filtruj po branży, cenie, parametrach SEO i zasięgach.", en: "Browse hundreds of verified portals across different categories. Filter by industry, price, SEO parameters and reach." },
      details: { pl: ["Filtrowanie po kategorii i cenie", "Podgląd statystyk portalu", "Porównywanie ofert", "Bezpośredni kontakt z wydawcą"], en: ["Filter by category and price", "Portal stats preview", "Compare offers", "Direct contact with publisher"] }
    },
    {
      icon: FileText,
      title: { pl: "Zamawianie artykułów", en: "Order Articles" },
      desc: { pl: "Zamów artykuł sponsorowany lub dostarcz własny tekst. Wybierz typ publikacji: artykuł, baner, link w treści lub wpis w social media.", en: "Order a sponsored article or submit your own text. Choose publication type: article, banner, in-text link or social media post." },
      details: { pl: ["Różne typy publikacji", "Upload własnych grafik", "Wybór kategorii docelowej", "Harmonogram publikacji"], en: ["Various publication types", "Upload custom graphics", "Target category selection", "Publication schedule"] }
    },
    {
      icon: Sparkles,
      title: { pl: "Generator AI treści", en: "AI Content Generator" },
      desc: { pl: "Wygeneruj profesjonalny artykuł za pomocą sztucznej inteligencji. Wybierz temat, ton i długość - reszta zostanie zrobiona automatycznie.", en: "Generate professional articles using artificial intelligence. Choose topic, tone and length - the rest is done automatically." },
      details: { pl: ["Generowanie pełnych artykułów", "Optymalizacja pod SEO", "Różne style pisania", "Edycja i personalizacja"], en: ["Full article generation", "SEO optimization", "Various writing styles", "Edit and personalize"] }
    },
    {
      icon: MessageSquare,
      title: { pl: "Negocjacje cen", en: "Price Negotiation" },
      desc: { pl: "Negocjuj ceny bezpośrednio z wydawcami. System negocjacji pozwala na uzyskanie lepszych warunków przy większych zamówieniach.", en: "Negotiate prices directly with publishers. The negotiation system allows you to get better terms for larger orders." },
      details: { pl: ["Chat z wydawcą", "Historia negocjacji", "Kontroferty", "Rabaty ilościowe"], en: ["Chat with publisher", "Negotiation history", "Counter-offers", "Volume discounts"] }
    },
    {
      icon: CreditCard,
      title: { pl: "Portfel Zorki", en: "Zorki Wallet" },
      desc: { pl: "Wirtualna waluta Epidemos. Doładuj portfel i płać za zamówienia bez prowizji. Możliwość ubiegania się o kredyt na publikacje.", en: "Epidemos virtual currency. Top up your wallet and pay for orders without fees. Option to apply for publication credit." },
      details: { pl: ["Doładowania PayPal", "Historia transakcji", "Kredyt na publikacje", "Brak ukrytych opłat"], en: ["PayPal top-ups", "Transaction history", "Publication credit", "No hidden fees"] }
    },
    {
      icon: BarChart3,
      title: { pl: "Panel raportów", en: "Reports Panel" },
      desc: { pl: "Śledź status swoich zamówień w czasie rzeczywistym. Pobieraj raporty z opublikowanych artykułów i monitoruj efekty.", en: "Track your order status in real-time. Download reports from published articles and monitor results." },
      details: { pl: ["Status zamówień", "Linki do publikacji", "Eksport raportów", "Historia współpracy"], en: ["Order status", "Publication links", "Export reports", "Collaboration history"] }
    },
  ];

  const publisherFeatures = [
    {
      icon: Building2,
      title: { pl: "Dodawanie portali", en: "Add Portals" },
      desc: { pl: "Dodaj swoje strony internetowe do marketplace. Ustaw cennik, kategorie i typy akceptowanych publikacji.", en: "Add your websites to the marketplace. Set pricing, categories and types of accepted publications." },
      details: { pl: ["Nieograniczona liczba stron", "Własny cennik", "Wybór kategorii", "Moderacja treści"], en: ["Unlimited sites", "Custom pricing", "Category selection", "Content moderation"] }
    },
    {
      icon: Plug,
      title: { pl: "Integracja API WordPress", en: "WordPress API Integration" },
      desc: { pl: "Jako PIERWSZA platforma w Polsce oferujemy pełną integrację z WordPress! Artykuły publikują się automatycznie na Twojej stronie.", en: "As the FIRST platform in Poland, we offer full WordPress integration! Articles are published automatically on your site." },
      details: { pl: ["Automatyczna publikacja", "Wybór kategorii WP", "Upload grafik", "Zero ręcznej pracy"], en: ["Auto publishing", "WP category selection", "Image upload", "Zero manual work"] },
      highlight: true
    },
    {
      icon: Settings,
      title: { pl: "Zarządzanie ofertami", en: "Manage Offers" },
      desc: { pl: "Twórz cenniki dla każdej strony osobno. Ustal ceny za artykuły, banery, linki i wpisy social media.", en: "Create pricing for each site separately. Set prices for articles, banners, links and social media posts." },
      details: { pl: ["Elastyczne cenniki", "Promocje czasowe", "Pakiety usług", "Indywidualne wyceny"], en: ["Flexible pricing", "Time promotions", "Service packages", "Individual quotes"] }
    },
    {
      icon: Eye,
      title: { pl: "Moderacja zamówień", en: "Order Moderation" },
      desc: { pl: "Przeglądaj i akceptuj zamówienia przed publikacją. Masz pełną kontrolę nad treściami na swoich portalach.", en: "Review and accept orders before publication. You have full control over content on your portals." },
      details: { pl: ["Podgląd artykułów", "Akceptacja/odrzucenie", "Prośba o poprawki", "Komentarze do zleceń"], en: ["Article preview", "Accept/reject", "Request changes", "Order comments"] }
    },
    {
      icon: TrendingUp,
      title: { pl: "Zarobki i wypłaty", en: "Earnings & Payouts" },
      desc: { pl: "Śledź swoje zarobki w panelu. Zamawiaj wypłaty na konto bankowe lub PayPal bez minimalnych progów.", en: "Track your earnings in the panel. Request payouts to bank account or PayPal with no minimum thresholds." },
      details: { pl: ["Raport zarobków", "Wypłaty natychmiastowe", "Historia płatności", "Fakturowanie"], en: ["Earnings report", "Instant payouts", "Payment history", "Invoicing"] }
    },
    {
      icon: Rss,
      title: { pl: "Źródła RSS", en: "RSS Sources" },
      desc: { pl: "Dodaj źródła RSS do automatycznego importu artykułów. System pobiera treści i przygotowuje je do publikacji.", en: "Add RSS sources for automatic article import. System fetches content and prepares it for publication." },
      details: { pl: ["Import z RSS", "Automatyczne formatowanie", "Kolejka artykułów", "Harmonogram publikacji"], en: ["RSS import", "Auto formatting", "Article queue", "Publication schedule"] }
    },
  ];

  const upcomingFeatures = [
    { icon: Bot, title: { pl: "Baza artykułów gościnnych", en: "Guest Article Database" }, desc: { pl: "Gotowe artykuły do pobrania z bazy. AI wyszukuje i przygotowuje treści dopasowane do Twojej branży.", en: "Ready articles to download from database. AI searches and prepares content matched to your industry." }, status: { pl: "W przygotowaniu", en: "In preparation" } },
    { icon: Newspaper, title: { pl: "Newsy znalezione przez AI", en: "AI-Found News" }, desc: { pl: "Automatyczne wyszukiwanie aktualnych newsów i trendów. AI generuje artykuły na podstawie bieżących wydarzeń.", en: "Automatic search for current news and trends. AI generates articles based on current events." }, status: { pl: "Wkrótce", en: "Soon" } },
    { icon: Database, title: { pl: "Baza linków i domen", en: "Links & Domains Database" }, desc: { pl: "Rozbudowana baza domen z parametrami SEO. Znajdź najlepsze miejsca do linkowania.", en: "Extensive database of domains with SEO parameters. Find the best places for linking." }, status: "Q2 2026" },
    { icon: Target, title: { pl: "Kampanie automatyczne", en: "Automatic Campaigns" }, desc: { pl: "Ustaw budżet i cele - AI dobierze najlepsze portale i rozłoży publikacje w czasie.", en: "Set budget and goals - AI will select the best portals and distribute publications over time." }, status: { pl: "Planowane", en: "Planned" } },
  ];

  const benefits = [
    { icon: Zap, text: { pl: "Oszczędność czasu - automatyzacja publikacji", en: "Time savings - publication automation" } },
    { icon: Shield, text: { pl: "Bezpieczeństwo - weryfikowane portale", en: "Security - verified portals" } },
    { icon: TrendingUp, text: { pl: "Efekty SEO - budowanie linków", en: "SEO effects - link building" } },
    { icon: Users, text: { pl: "Sieć wydawców - setki portali", en: "Publisher network - hundreds of portals" } },
    { icon: Sparkles, text: { pl: "AI - generowanie treści", en: "AI - content generation" } },
    { icon: Clock, text: { pl: "24/7 - dostęp do platformy", en: "24/7 - platform access" } },
  ];

  // Dynamiczne statystyki z API
  const displayStats = [
    { value: stats.portals > 0 ? `${stats.portals}+` : "---", label: { pl: "Portali w sieci", en: "Portals in network" } },
    { value: stats.articles > 0 ? `${stats.articles.toLocaleString()}+` : "---", label: { pl: "Opublikowanych artykułów", en: "Published articles" } },
    { value: stats.advertisers > 0 ? `${stats.advertisers}+` : "---", label: { pl: "Reklamodawców", en: "Advertisers" } },
    { value: "4", label: { pl: "Modele AI", en: "AI Models" } },
  ];

  const advertiserSteps = [
    { pl: "Załóż konto i doładuj portfel Zorki", en: "Create account and top up Zorki wallet" },
    { pl: "Przeglądaj marketplace i wybierz portal", en: "Browse marketplace and choose portal" },
    { pl: "Zamów artykuł lub wygeneruj go przez AI", en: "Order article or generate with AI" },
    { pl: "Dodaj grafikę i wybierz datę publikacji", en: "Add graphics and select publication date" },
    { pl: "Zatwierdź i czekaj na publikację", en: "Approve and wait for publication" },
    { pl: "Otrzymaj link do opublikowanego artykułu", en: "Receive link to published article" },
  ];

  const publisherSteps = [
    { pl: "Załóż konto i dodaj swoje portale", en: "Create account and add your portals" },
    { pl: "Skonfiguruj cennik i typy publikacji", en: "Configure pricing and publication types" },
    { pl: "Połącz WordPress przez API (opcjonalnie)", en: "Connect WordPress via API (optional)" },
    { pl: "Otrzymuj zamówienia od reklamodawców", en: "Receive orders from advertisers" },
    { pl: "Akceptuj lub proponuj poprawki", en: "Accept or suggest changes" },
    { pl: "Artykuł publikuje się automatycznie!", en: "Article publishes automatically!" },
  ];

  const wpFeatures = [
    { pl: "Artykuły publikują się automatycznie po akceptacji", en: "Articles publish automatically after approval" },
    { pl: "Grafiki przesyłane bezpośrednio do biblioteki mediów", en: "Images uploaded directly to media library" },
    { pl: "Wybór kategorii WordPress przy zamawianiu", en: "WordPress category selection when ordering" },
    { pl: "Zero ręcznej pracy - pełna automatyzacja", en: "Zero manual work - full automation" },
    { pl: "Bezpieczne połączenie przez REST API", en: "Secure connection via REST API" },
    { pl: "Integracja z innymi CMS-ami na życzenie - wystarczy zgłosić!", en: "Integration with other CMSs on request - just let us know!" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Header onLogin={() => window.location.href = "/login"} />

      {/* Hero Section */}
      <section className="pt-24 py-16 md:py-24 px-4 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <Badge className="bg-white/20 text-white border-0 mb-6 px-4 py-1.5">
            <Rocket className="w-4 h-4 mr-2" /> {language === 'pl' ? 'Poznaj wszystkie możliwości' : 'Discover all features'}
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            {language === 'pl' ? 'Wszystko czego potrzebujesz' : 'Everything you need'}<br />
            {language === 'pl' ? 'do content marketingu' : 'for content marketing'}
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-10">
            {language === 'pl' 
              ? 'Epidemos to kompletna platforma łącząca reklamodawców z wydawcami. Automatyzacja, AI, integracje API - wszystko w jednym miejscu.'
              : 'Epidemos is a complete platform connecting advertisers with publishers. Automation, AI, API integrations - all in one place.'}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50 shadow-xl">
                <Play className="w-5 h-5 mr-2" /> {language === 'pl' ? 'Zacznij za darmo' : 'Get started free'}
              </Button>
            </Link>
            <a href="#features">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                {language === 'pl' ? 'Zobacz funkcje' : 'View features'} <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 px-4 bg-white border-b border-slate-100">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {displayStats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className={`text-4xl font-bold text-blue-600 mb-1 ${loadingStats ? 'animate-pulse' : ''}`}>{stat.value}</div>
                <div className="text-sm text-slate-500">{stat.label[language]}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Bar */}
      <section className="py-8 px-4 bg-slate-50 border-b border-slate-100">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap justify-center gap-6 md:gap-10">
            {benefits.map((b, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                <b.icon className="w-5 h-5 text-blue-600" />
                <span>{b.text[language]}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {language === 'pl' ? 'Funkcje dopasowane do Twoich potrzeb' : 'Features tailored to your needs'}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {language === 'pl' 
                ? 'Wybierz swoją rolę i poznaj wszystkie narzędzia, które dla Ciebie przygotowaliśmy.'
                : 'Choose your role and discover all the tools we\'ve prepared for you.'}
            </p>
          </div>

          <Tabs value={activeRole} onValueChange={setActiveRole} className="w-full">
            <div className="flex justify-center mb-10">
              <TabsList className="grid grid-cols-2 w-full max-w-md h-14 p-1.5 bg-slate-100">
                <TabsTrigger value="advertiser" className="h-full text-base data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg">
                  <Megaphone className="w-5 h-5 mr-2" /> {language === 'pl' ? 'Reklamodawca' : 'Advertiser'}
                </TabsTrigger>
                <TabsTrigger value="publisher" className="h-full text-base data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-lg">
                  <Building2 className="w-5 h-5 mr-2" /> {language === 'pl' ? 'Wydawca' : 'Publisher'}
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="advertiser" className="mt-0">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {advertiserFeatures.map((feature, i) => (
                  <Card key={i} className="group hover:shadow-xl transition-all duration-300 border-slate-200 hover:border-blue-200 bg-white">
                    <CardHeader>
                      <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center mb-4 group-hover:bg-blue-600 transition-colors">
                        <feature.icon className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors" />
                      </div>
                      <CardTitle className="text-xl">{feature.title[language]}</CardTitle>
                      <CardDescription className="text-base">{feature.desc[language]}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {feature.details[language].map((detail, j) => (
                          <li key={j} className="flex items-center gap-2 text-sm text-slate-600">
                            <CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0" />
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="publisher" className="mt-0">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {publisherFeatures.map((feature, i) => (
                  <Card key={i} className={`group hover:shadow-xl transition-all duration-300 bg-white ${feature.highlight ? 'border-2 border-green-500 ring-4 ring-green-100' : 'border-slate-200 hover:border-green-200'}`}>
                    <CardHeader>
                      {feature.highlight && (
                        <Badge className="w-fit mb-3 bg-green-100 text-green-700 border-green-200">
                          <Star className="w-3 h-3 mr-1" /> {language === 'pl' ? 'Unikalna funkcja' : 'Unique feature'}
                        </Badge>
                      )}
                      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-colors ${feature.highlight ? 'bg-green-600' : 'bg-green-100 group-hover:bg-green-600'}`}>
                        <feature.icon className={`w-7 h-7 transition-colors ${feature.highlight ? 'text-white' : 'text-green-600 group-hover:text-white'}`} />
                      </div>
                      <CardTitle className="text-xl">{feature.title[language]}</CardTitle>
                      <CardDescription className="text-base">{feature.desc[language]}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {feature.details[language].map((detail, j) => (
                          <li key={j} className="flex items-center gap-2 text-sm text-slate-600">
                            <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* WordPress Integration Section */}
      <section className="py-20 px-4 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 mb-6">
                <Plug className="w-4 h-4 mr-2" /> {language === 'pl' ? 'Pierwsza platforma w Polsce' : 'First platform in Poland'}
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                {language === 'pl' ? 'Pełna integracja API z WordPress' : 'Full WordPress API Integration'}
              </h2>
              <p className="text-lg text-slate-300 mb-8">
                {language === 'pl' 
                  ? 'Jako jedyna platforma w Polsce oferujemy automatyczną publikację artykułów bezpośrednio na Twojej stronie WordPress. Koniec z ręcznym kopiowaniem treści!'
                  : 'As the only platform in Poland, we offer automatic publication of articles directly on your WordPress site. No more manual content copying!'}
              </p>
              <ul className="space-y-3 mb-8">
                {wpFeatures.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">{feature[language]}</span>
                  </li>
                ))}
              </ul>
              <Link to="/publisher">
                <Button size="lg" className="bg-green-600 hover:bg-green-700">
                  {language === 'pl' ? 'Połącz swój WordPress' : 'Connect your WordPress'} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <div className="mt-6 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                <p className="text-sm text-slate-400">
                  <span className="text-blue-400 font-medium">{language === 'pl' ? 'Masz inny CMS?' : 'Have another CMS?'}</span> {language === 'pl' ? 'Nie problem! Oprócz WordPressa możemy zintegrować się z każdym systemem posiadającym API.' : 'No problem! Besides WordPress, we can integrate with any system that has an API.'}
                  <span className="text-white font-medium"> Joomla, Drupal, PrestaShop, {language === 'pl' ? 'własne CMS-y' : 'custom CMSs'}</span> - {language === 'pl' ? 'wystarczy, że nam zgłosisz, a przygotujemy integrację dla Twojego portalu.' : 'just let us know and we\'ll prepare an integration for your portal.'}
                </p>
                <a href="mailto:bok@epidemos.pl" className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 mt-2 text-sm">
                  {language === 'pl' ? 'Napisz do nas: bok@epidemos.pl' : 'Contact us: bok@epidemos.pl'} <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
            <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <pre className="text-sm text-slate-300 overflow-x-auto">
                <code>{`// ${language === 'pl' ? 'Automatyczna publikacja artykułu' : 'Automatic article publication'}
POST /wp-json/wp/v2/posts

{
  "title": "${language === 'pl' ? 'Artykuł sponsorowany' : 'Sponsored Article'}",
  "content": "<p>${language === 'pl' ? 'Treść artykułu...' : 'Article content...'}</p>",
  "status": "publish",
  "categories": [12, 45],
  "featured_media": 1234
}

// ${language === 'pl' ? 'Odpowiedź' : 'Response'}: 201 Created
{
  "id": 5678,
  "link": "https://${language === 'pl' ? 'twoja-strona.pl/artykul' : 'your-site.com/article'}",
  "status": "publish"
}`}</code>
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap Section */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">
              <Calendar className="w-4 h-4 mr-2" /> {language === 'pl' ? 'Roadmapa' : 'Roadmap'}
            </Badge>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">{language === 'pl' ? 'Nadchodzące funkcje' : 'Coming Soon'}</h2>
            <p className="text-lg text-slate-600">{language === 'pl' ? 'Ciągle rozwijamy platformę. Oto co planujemy wdrożyć w najbliższym czasie.' : 'We\'re constantly developing the platform. Here\'s what we plan to implement soon.'}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {upcomingFeatures.map((feature, i) => (
              <Card key={i} className="bg-white/50 border-dashed border-2 border-slate-200">
                <CardHeader>
                  <Badge variant="outline" className="w-fit mb-3 text-xs">
                    <Lock className="w-3 h-3 mr-1" /> {typeof feature.status === 'string' ? feature.status : feature.status[language]}
                  </Badge>
                  <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-slate-400" />
                  </div>
                  <CardTitle className="text-lg text-slate-700">{feature.title[language]}</CardTitle>
                  <CardDescription>{feature.desc[language]}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">{language === 'pl' ? 'Jak to działa?' : 'How it works?'}</h2>
            <p className="text-lg text-slate-600">{language === 'pl' ? 'Prosty proces w kilku krokach' : 'Simple process in a few steps'}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Advertiser Steps */}
            <div className="bg-blue-50 rounded-2xl p-8 border border-blue-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
                  <Megaphone className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">{language === 'pl' ? 'Dla reklamodawcy' : 'For advertiser'}</h3>
              </div>
              <ol className="space-y-4">
                {advertiserSteps.map((step, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {i + 1}
                    </div>
                    <span className="text-slate-700 pt-1">{step[language]}</span>
                  </li>
                ))}
              </ol>
            </div>
            {/* Publisher Steps */}
            <div className="bg-green-50 rounded-2xl p-8 border border-green-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-green-600 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">{language === 'pl' ? 'Dla wydawcy' : 'For publisher'}</h3>
              </div>
              <ol className="space-y-4">
                {publisherSteps.map((step, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {i + 1}
                    </div>
                    <span className="text-slate-700 pt-1">{step[language]}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">{language === 'pl' ? 'Gotowy zacząć?' : 'Ready to start?'}</h2>
          <p className="text-xl text-blue-100 mb-8">
            {language === 'pl' 
              ? 'Dołącz do grona reklamodawców i wydawców korzystających z Epidemos. Rejestracja jest darmowa!'
              : 'Join the group of advertisers and publishers using Epidemos. Registration is free!'}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50">
                {language === 'pl' ? 'Załóż darmowe konto' : 'Create free account'} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/shop-public">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                {language === 'pl' ? 'Przeglądaj marketplace' : 'Browse marketplace'}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
